import React, { useState, useEffect } from 'react';
import { logger } from './lib/logger';
import { X, ChevronDown, ChevronRight, Plus, Folder, FolderOpen, FileText, Edit3, Trash2, Check, AlertCircle, Inbox, Palette, Share2, RotateCcw, Clock, Copy } from 'lucide-react';
import supabase from './supabase';
import { getRoleBadge } from './types/sharingUtils';
import ShareProjectDialog from './ShareProjectDialog';
import { useT } from './i18n/index';
import type { ProjectWithFlowcharts, FlowchartWithAccess } from './types';
import type { SavedFlowchart } from './types';

interface ProjectListProps {
  user: { id: string; email?: string };
  onClose: () => void;
  onLoadFlowchart: (flowchart: SavedFlowchart) => void;
  onSetProject?: (projectId: string | null, projectName: string | null) => void;
  onDeleteFlowchart: (id: string) => void;
  onRestoreVersion?: (versionData: SavedFlowchart, flowchart: FlowchartWithAccess) => void;
  currentFlowchartId?: string;
  /** When set, only items of this type are shown (e.g. 'mindmap'). */
  filterMapType?: 'flowchart' | 'mindmap';
}

interface TrashedFlowchart {
  id: string;
  title: string;
  deleted_at: string;
  user_id: string;
}

interface VersionItem {
  id: string;
  title: string;
  saved_at: string;
  saved_by: string;
}

interface ConfirmDialogProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  isDangerous?: boolean;
}

const ConfirmDialog = ({ title, message, onConfirm, onCancel, confirmText = 'Confirm', cancelText = 'Cancel', isDangerous = false }: ConfirmDialogProps) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }} onClick={onCancel}>
    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" style={{ background: 'white' }} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start gap-3 mb-4">
        <AlertCircle className={isDangerous ? 'text-red-500' : 'text-blue-500'} size={24} />
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-2">{title}</h3>
          <p className="text-gray-600 text-sm">{message}</p>
        </div>
      </div>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
        >
          {cancelText}
        </button>
        <button
          onClick={() => {
            onConfirm();
            onCancel();
          }}
          className={`px-4 py-2 rounded-lg text-white transition-colors font-medium ${
            isDangerous 
              ? 'bg-red-500 hover:bg-red-600' 
              : 'bg-blue-500 hover:bg-blue-600'
          }`}
        >
          {confirmText}
        </button>
      </div>
    </div>
  </div>
);

const PROJECT_COLORS = [
  { hex: '#3b82f6', name: 'Blue' },
  { hex: '#10b981', name: 'Green' },
  { hex: '#f59e0b', name: 'Amber' },
  { hex: '#ef4444', name: 'Red' },
  { hex: '#8b5cf6', name: 'Purple' },
  { hex: '#ec4899', name: 'Pink' },
  { hex: '#06b6d4', name: 'Cyan' },
  { hex: '#84cc16', name: 'Lime' },
];

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, opacity: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export default function ProjectList({
  user,
  onClose,
  onLoadFlowchart,
  onSetProject,
  onDeleteFlowchart,
  onRestoreVersion,
  currentFlowchartId,
  filterMapType,
}: ProjectListProps): JSX.Element {
  const { t } = useT();
  const [projects, setProjects] = useState<ProjectWithFlowcharts[]>([]);
  const [ungroupedFlowcharts, setUngroupedFlowcharts] = useState<FlowchartWithAccess[]>([]);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectColor, setNewProjectColor] = useState('#3b82f6');
  const [editingProject, setEditingProject] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editingColor, setEditingColor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogProps | null>(null);
  const [sharingProject, setSharingProject] = useState<{ id: string; name: string; color: string } | null>(null);
  const [newFlowchartInProject, setNewFlowchartInProject] = useState<string | null>(null);
  const [newFlowchartTitle, setNewFlowchartTitle] = useState('');
  const [trashedFlowcharts, setTrashedFlowcharts] = useState<TrashedFlowchart[]>([]);
  const [showTrash, setShowTrash] = useState(false);
  const [versionHistoryFor, setVersionHistoryFor] = useState<FlowchartWithAccess | null>(null);
  const [versionItems, setVersionItems] = useState<VersionItem[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Load projects and flowcharts
  const loadData = async () => {
    try {
      setLoading(true);

      // These three queries are independent of each other, so run them concurrently
      // instead of one-after-another — on a slow/high-latency connection (e.g. a
      // corporate VPN/proxy) three sequential round-trips compound into a multi-second
      // wait, which is exactly what shows up as the project list appearing "stuck" on
      // its loading state.
      const [
        { data: projectsData, error: projectsError },
        { data: flowchartsData, error: flowchartsError },
        { data: trashData },
      ] = await Promise.all([
        // Load all projects with access
        supabase
          .from('projects')
          .select(`
            id, name, color, created_at, user_id,
            project_access!inner(role)
          `)
          .eq('project_access.user_id', user.id)
          .order('created_at', { ascending: false }),

        // Load all flowcharts visible via RLS — only metadata, no boxes/arrows payload
        supabase
          .from('flowcharts')
          .select(`
            id, title, updated_at, created_at, project_id, map_type, user_id,
            flowchart_access!left(role)
          `)
          .eq('flowchart_access.user_id', user.id)
          .is('deleted_at', null)
          .order('updated_at', { ascending: false }),

        // Load trash (soft-deleted, only own flowcharts)
        supabase
          .from('flowcharts')
          .select('id, title, deleted_at, user_id')
          .eq('user_id', user.id)
          .not('deleted_at', 'is', null)
          .order('deleted_at', { ascending: false }),
      ]);

      if (projectsError) throw projectsError;
      if (flowchartsError) throw flowchartsError;
      setTrashedFlowcharts(trashData || []);

      // Build projectId → access_role lookup for inherited role fallback
      const projectRoleMap: Record<string, string> = {};
      (projectsData || []).forEach((p) => {
        const acc = Array.isArray(p.project_access) ? p.project_access[0] : p.project_access;
        projectRoleMap[p.id] = acc?.role || 'viewer';
      });

      // Mindmaps are only shown when filterMapType='mindmap', otherwise always excluded
      const visibleFlowcharts = filterMapType === 'mindmap'
        ? (flowchartsData || []).filter(f => f.map_type === 'mindmap')
        : (flowchartsData || []).filter(f => f.map_type !== 'mindmap');

      // Group flowcharts by project
      const projectsWithFlowcharts = (projectsData || []).map(p => {
        const projectFlowcharts = visibleFlowcharts
          .filter(f => f.project_id === p.id)
          .map(f => {
            const access = Array.isArray(f.flowchart_access)
              ? f.flowchart_access[0]
              : f.flowchart_access;
            // When no direct flowchart_access row exists, inherit the project role
            const role = access?.role || projectRoleMap[f.project_id] || 'viewer';
            return {
              ...f,
              access_role: role
            } as unknown as FlowchartWithAccess;
          });

        const access = Array.isArray(p.project_access) 
          ? p.project_access[0] 
          : p.project_access;

        return {
          ...p,
          access_role: access?.role || 'viewer',
          flowcharts: projectFlowcharts,
          flowchart_count: projectFlowcharts.length
        };
      });

      // Get ungrouped flowcharts — includes flowcharts in a project the user has no project_access to
      // (e.g. a flowchart shared directly, later moved to a project by the owner)
      const loadedProjectIds = new Set((projectsData || []).map((p: { id: string }) => p.id));
      const ungrouped = visibleFlowcharts
        .filter(f => !f.project_id || !loadedProjectIds.has(f.project_id))
        .map(f => {
          const access = Array.isArray(f.flowchart_access) 
            ? f.flowchart_access[0] 
            : f.flowchart_access;
          return {
            ...f,
            access_role: access?.role || 'viewer'
          } as unknown as FlowchartWithAccess;
        });

      setProjects(projectsWithFlowcharts as unknown as ProjectWithFlowcharts[]);
      setUngroupedFlowcharts(ungrouped);


    } catch (error) {
      logger.error('Error loading projects:', error);
      alert(t('loadProjectsFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openVersionHistory = async (flowchart: FlowchartWithAccess) => {
    setVersionHistoryFor(flowchart);
    setVersionItems([]);
    setLoadingVersions(true);
    const { data } = await supabase
      .from('flowchart_versions')
      .select('id, title, saved_at, saved_by')
      .eq('flowchart_id', flowchart.id)
      .order('saved_at', { ascending: false })
      .limit(30);
    setVersionItems(data || []);
    setLoadingVersions(false);
  };

  const restoreVersion = async (versionId: string) => {
    if (!versionHistoryFor || !onRestoreVersion) return;
    if (!window.confirm(t('confirmRestoreVersion'))) return;
    const { data, error } = await supabase
      .from('flowchart_versions')
      .select('*')
      .eq('id', versionId)
      .single();
    if (error || !data) { alert(t('couldNotLoadVersion')); return; }
    onRestoreVersion(data, versionHistoryFor);
    setVersionHistoryFor(null);
  };

  // Toggle project expansion
  const toggleProject = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  // Create new project
  const createProject = async () => {
    if (!newProjectName.trim()) {
      alert(t('enterProjectName'));
      return;
    }

    // Check project limit
    if (projects.length >= 10) {
      alert(t('maxProjects'));
      return;
    }

    try {
      const { data, error } = await supabase
        .from('projects')
        .insert({
          name: newProjectName.trim(),
          color: newProjectColor,
          user_id: user.id
        })
        .select()
        .single();

      if (error) throw error;

      // Create share settings
      const shareToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      
      await supabase
        .from('project_share_settings')
        .insert({
          project_id: data.id,
          share_token: shareToken,
          link_sharing_enabled: false
        });

      setNewProjectName('');
      setShowNewProject(false);
      await loadData();

      // Expand the new project
      setExpandedProjects(prev => new Set([...prev, data.id]));
    } catch (error) {
      logger.error('Error creating project:', error);
      alert(t('createProjectFailed'));
    }
  };

  // Update project name
  const updateProjectName = async (projectId: string) => {
    if (!editName.trim()) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({ name: editName.trim() })
        .eq('id', projectId);

      if (error) throw error;

      setEditingProject(null);
      setEditName('');
      await loadData();
    } catch (error) {
      logger.error('Error updating project:', error);
      alert(t('updateProjectFailed'));
    }
  };

  // Update project color
  const updateProjectColor = async (projectId: string, color: string) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({ color })
        .eq('id', projectId);

      if (error) throw error;

      setEditingColor(null);
      await loadData();
    } catch (error) {
      logger.error('Error updating project color:', error);
      alert(t('updateProjectColorFailed'));
    }
  };

  // Delete project
  const deleteProject = async (projectId: string, hasFlowcharts: boolean) => {
    const confirmDelete = () => {
      doDeleteProject(projectId);
    };

    setConfirmDialog({
      title: t('deleteProject'),
      message: hasFlowcharts
        ? t('confirmDeleteProjectWithFlowcharts')
        : t('confirmDeleteProject'),
      onConfirm: confirmDelete,
      onCancel: () => setConfirmDialog(null),
      confirmText: t('delete'),
      cancelText: t('cancel'),
      isDangerous: true
    });
  };

  const doDeleteProject = async (projectId: string) => {
    try {
      // Move flowcharts to ungrouped
      await supabase
        .from('flowcharts')
        .update({ project_id: null })
        .eq('project_id', projectId);

      // Delete project (will cascade delete access and share settings)
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      await loadData();
    } catch (error) {
      logger.error('Error deleting project:', error);
      alert(t('deleteProjectFailed'));
    }
  };

  // Leave project (for non-creators who are owner via project_access)
  const leaveProject = async (projectId: string) => {
    setConfirmDialog({
      title: t('leaveProject'),
      message: t('confirmLeaveProject'),
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('project_access')
            .delete()
            .eq('project_id', projectId)
            .eq('user_id', user.id);
          if (error) throw error;
          await loadData();
        } catch (err: any) {
          logger.error('Error leaving project:', err);
          alert(t('leaveProjectFailed'));
        }
      },
      onCancel: () => setConfirmDialog(null),
      confirmText: t('leave'),
      cancelText: t('cancel'),
      isDangerous: true,
    });
  };

  // Leave a shared flowchart/mindmap you only have viewer access to. Unlike
  // handleDeleteFlowchart (which soft-deletes the row for its owner and every other
  // collaborator too, and which RLS blocks for viewers anyway — can_edit_flowchart
  // requires editor/owner), this only removes the current user's own
  // flowchart_access row, so the item simply stops showing up in their own list.
  const leaveFlowchart = async (flowchartId: string) => {
    setConfirmDialog({
      title: t('leaveFlowchart'),
      message: t('confirmLeaveFlowchart'),
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('flowchart_access')
            .delete()
            .eq('flowchart_id', flowchartId)
            .eq('user_id', user.id);
          if (error) throw error;
          await loadData();
        } catch (err: any) {
          logger.error('Error leaving flowchart:', err);
          alert(t('leaveFlowchartFailed'));
        }
      },
      onCancel: () => setConfirmDialog(null),
      confirmText: t('leave'),
      cancelText: t('cancel'),
      isDangerous: true,
    });
  };

  // Move flowchart to project
  const moveFlowchart = async (flowchartId: string, targetProjectId: string | null) => {
    try {
      const { error } = await supabase
        .from('flowcharts')
        .update({ project_id: targetProjectId })
        .eq('id', flowchartId);

      if (error) throw error;

      await loadData();
    } catch (error) {
      logger.error('Error moving flowchart:', error);
      alert(t('moveFlowchartFailed'));
    }
  };

  const handleDeleteFlowchart = async (id: string) => {
    await onDeleteFlowchart(id);
    await loadData();
  };

  const duplicateFlowchart = async (flowchart: FlowchartWithAccess) => {
    const { data: full, error } = await supabase
      .from('flowcharts')
      .select('*')
      .eq('id', flowchart.id)
      .single();
    if (error || !full) { alert(t('duplicateFailed')); return; }
    // Copy every column by default (via the rest-spread below) rather than hand-picking
    // a fixed list — the old version named each column explicitly and, as new
    // settings columns were added to `flowcharts` over time (resources, non_working_days,
    // work_days, the gantt_show_*/canvas_show_* view toggles, the timeline/export-row
    // presets, ...), this function was never updated to match, so a duplicate silently
    // reset every one of them to its column default instead of copying the original —
    // reported as "resources disappeared after duplicating" but not actually specific
    // to resources. Explicitly exclude only what must NOT carry over as-is: identity/
    // bookkeeping columns (id/created_at/updated_at/deleted_at/version/updated_by_email)
    // and the three fields this function already overrides below (title/user_id/
    // gantt_start_date).
    const {
      id: _id, created_at: _createdAt, updated_at: _updatedAt, deleted_at: _deletedAt,
      version: _version, updated_by_email: _updatedByEmail,
      title: _title, user_id: _userId, gantt_start_date, ...rest
    } = full;
    const { data: inserted, error: insertError } = await supabase
      .from('flowcharts')
      .insert({
        ...rest,
        title: `Copy of ${full.title || 'Untitled'}`,
        user_id: user.id,
        // Inherit the original's Gantt start date so a duplicate keeps the same
        // planning anchor — falling back to today only for an original that
        // somehow still has none (see FlowchartBuilder.tsx's save() for why every
        // flowchart should have one from creation on).
        gantt_start_date: gantt_start_date ?? new Date().toISOString().slice(0, 10),
      })
      .select()
      .single();
    if (insertError || !inserted) { alert(t('duplicateFailed')); return; }
    const shareToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    await supabase.from('flowchart_share_settings').insert({
      flowchart_id: inserted.id,
      share_token: shareToken,
      link_sharing_enabled: false,
    });
    // Carry over the DUPLICATING user's own view/display settings (Gantt zoom,
    // label width, show-subitems, end-time-display, resource filter, ...) onto
    // the new copy — same select-then-insert-with-denylist pattern as the
    // flowchart row itself above, so a duplicate opens looking the way THIS
    // user already had it configured instead of resetting to every default.
    // Best-effort, same as the share-settings insert right above: a brand new
    // flowchart id can never already have a (user_id, flowchart_id) row, and
    // nothing here should block the duplicate itself from succeeding.
    const { data: viewSettings } = await supabase
      .from('user_flowchart_view_settings')
      .select('*')
      .eq('user_id', user.id)
      .eq('flowchart_id', flowchart.id)
      .maybeSingle();
    if (viewSettings) {
      const { user_id: _vUserId, flowchart_id: _vFlowchartId, updated_at: _vUpdatedAt, ...viewRest } = viewSettings;
      await supabase.from('user_flowchart_view_settings').insert({
        ...viewRest,
        user_id: user.id,
        flowchart_id: inserted.id,
      });
    }
    await loadData();
  };

  // Restore a soft-deleted flowchart from trash
  const restoreFlowchart = async (id: string) => {
    try {
      const { error } = await supabase
        .from('flowcharts')
        .update({ deleted_at: null })
        .eq('id', id);
      if (error) throw error;
      await loadData();
    } catch (error: any) {
      alert(t('restoreFlowchartFailed'));
    }
  };

  // Permanently delete a flowchart from trash
  const permanentDeleteFlowchart = async (id: string) => {
    setConfirmDialog({
      title: t('deletePermanently'),
      message: t('confirmPermanentDelete'),
      onConfirm: async () => {
        try {
          const { error } = await supabase.from('flowcharts').delete().eq('id', id);
          if (error) throw error;
          await loadData();
        } catch (error: any) {
          alert(t('deleteFailed'));
        }
      },
      onCancel: () => setConfirmDialog(null),
      confirmText: t('deleteForever'),
      cancelText: t('cancel'),
      isDangerous: true,
    });
  };

  // Create new flowchart directly in a project
  const createFlowchartInProject = async (projectId: string) => {
    const isMindmap = filterMapType === 'mindmap';
    const title = newFlowchartTitle.trim() || (isMindmap ? `${t('untitled')} ${t('mindmaps')}` : `${t('untitled')} ${t('flowMode')}`);

    try {
      const freshRoot = { id: 1, text: t('centralTopic'), parentId: null, collapsed: false, color: '#6366f1' };
      const payload = {
        title,
        project_id: projectId,
        user_id: user.id,
        data: isMindmap ? JSON.stringify({ nextId: 2 }) : '{}',
        boxes: isMindmap ? JSON.stringify([freshRoot]) : JSON.stringify([]),
        arrows: JSON.stringify([]),
        departments: JSON.stringify([]),
        ...(isMindmap
          ? { map_type: 'mindmap' as const }
          // Default the Gantt start date to today right away — see
          // FlowchartBuilder.tsx's save() for why a null one isn't a safe default.
          : { view_mode: 'advanced', duration_unit: 'days', gantt_start_date: new Date().toISOString().slice(0, 10) }),
      };
      const { data, error } = await supabase
        .from('flowcharts')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;

      // NOTE: Owner access is automatically created by database trigger
      // No need to manually insert into flowchart_access

      setNewFlowchartInProject(null);
      setNewFlowchartTitle('');

      const project = projects.find(p => p.id === projectId);
      const parsedBoxes = data.boxes ? (typeof data.boxes === 'string' ? JSON.parse(data.boxes) : data.boxes) : [];
      onLoadFlowchart({ ...data, boxes: parsedBoxes, arrows: [], departments: [] } as SavedFlowchart);
      onSetProject?.(projectId, project?.name || null);
      onClose();
    } catch (error: any) {
      logger.error('Error creating flowchart:', error);
      alert(t('createFlowchartFailed'));
    }
  };

  const totalProjects = projects.length;
  const totalFlowcharts = projects.reduce((sum, p) => sum + p.flowchart_count, 0) + ungroupedFlowcharts.length;

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'white', borderRadius: 16, padding: 40, display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <div className="text-gray-600 font-medium">{t('loadingProjects')}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 860, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.35)', animation: 'modalSlideIn 0.18s ease-out' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e5e7eb', background: 'linear-gradient(to right, #f8faff, #fff)', flexShrink: 0 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
              {filterMapType === 'mindmap' ? t('mindmaps') : t('projectsAndFlowcharts')}
            </h2>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, display: 'flex', alignItems: 'center', gap: 16 }}>
              {!filterMapType && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Folder size={13} />
                  {totalProjects !== 1 ? t('projectsCountPlural', { count: totalProjects }) : t('projectsCount', { count: totalProjects })}
                </span>
              )}
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <FileText size={13} />
                {filterMapType === 'mindmap'
                  ? (totalFlowcharts !== 1 ? t('mindmapsCountPlural', { count: totalFlowcharts }) : t('mindmapsCount', { count: totalFlowcharts }))
                  : (totalFlowcharts !== 1 ? t('flowchartsCountPlural', { count: totalFlowcharts }) : t('flowchartsCount', { count: totalFlowcharts }))}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', borderRadius: 8, padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}
            onMouseOver={e => (e.currentTarget.style.background = '#f3f4f6')}
            onMouseOut={e => (e.currentTarget.style.background = 'none')}
          >
            <X size={20} />
          </button>
        </div>

        {/* New Project Button — hidden in mindmap mode */}
        <div className="px-6 pt-6">
          {!filterMapType && !showNewProject ? (
            <button
              onClick={() => setShowNewProject(true)}
              disabled={projects.length >= 10}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium shadow-sm hover:shadow-md"
            >
              <Plus size={20} />
              {projects.length >= 10 ? t('newProjectMaxReached') : t('newProject')}
            </button>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 space-y-4 border border-blue-200 shadow-sm">
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder={t('projectNamePlaceholder')}
                className="w-full px-4 py-2 border-2 border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && createProject()}
              />
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">{t('chooseColor')}</div>
                <div className="flex gap-2 flex-wrap">
                  {PROJECT_COLORS.map(color => (
                    <button
                      key={color.hex}
                      onClick={() => setNewProjectColor(color.hex)}
                      className={`relative w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                        newProjectColor === color.hex 
                          ? 'border-gray-900 scale-110 shadow-md' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: color.hex }}
                      title={color.name}
                    >
                      {newProjectColor === color.hex && (
                        <Check size={20} className="absolute inset-0 m-auto text-white drop-shadow" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={createProject}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium shadow-sm"
                >
                  {t('createProject')}
                </button>
                <button
                  onClick={() => {
                    setShowNewProject(false);
                    setNewProjectName('');
                  }}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Project List */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {/* Projects */}
          {projects.map(project => {
            const isExpanded = expandedProjects.has(project.id);
            const roleBadge = getRoleBadge(project.access_role);
            const bgColor = hexToRgba(project.color, 0.1);

            return (
              <div 
                key={project.id} 
                className="border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Project Header - with background color */}
                <div 
                  className="flex items-center gap-3 p-4 hover:brightness-95 transition-all group"
                  style={{ backgroundColor: bgColor }}
                >
                  <button
                    onClick={() => toggleProject(project.id)}
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </button>

                  {editingProject === project.id ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={() => updateProjectName(project.id)}
                      onKeyPress={(e) => e.key === 'Enter' && updateProjectName(project.id)}
                      className="flex-1 px-3 py-1 border-2 border-blue-300 rounded-lg focus:outline-none focus:border-blue-500"
                      autoFocus
                    />
                  ) : (
                    <button
                      onClick={() => toggleProject(project.id)}
                      className="flex-1 text-left font-semibold flex items-center gap-2 text-gray-800 hover:text-blue-600 transition-colors"
                    >
                      {isExpanded ? <FolderOpen size={18} /> : <Folder size={18} />}
                      {project.name}
                      <span className="text-xs text-gray-400 font-normal bg-white bg-opacity-80 px-2 py-0.5 rounded-full">
                        {project.flowchart_count}
                      </span>
                    </button>
                  )}

                  {/* Role badge */}
                  <div className={`text-xs px-2 py-1 rounded-full font-medium ${roleBadge.color}`}>
                    {roleBadge.label}
                  </div>

                  {/* Actions */}
                  {project.access_role === 'owner' && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setNewFlowchartInProject(project.id);
                          setNewFlowchartTitle('');
                          if (!isExpanded) toggleProject(project.id);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-white rounded-lg transition-colors"
                        title={filterMapType === 'mindmap' ? t('newMindmapInProject') : t('newFlowchartInProject')}
                      >
                        <Plus size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSharingProject({ id: project.id, name: project.name, color: project.color });
                        }}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-white rounded-lg transition-colors"
                        title={t('shareProject')}
                      >
                        <Share2 size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingColor(editingColor === project.id ? null : project.id);
                        }}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-white rounded-lg transition-colors"
                        title={t('changeColor')}
                      >
                        <Palette size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingProject(project.id);
                          setEditName(project.name);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-white rounded-lg transition-colors"
                        title={t('renameProject')}
                      >
                        <Edit3 size={16} />
                      </button>
                      {project.user_id === user.id ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteProject(project.id, project.flowchart_count > 0);
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                          title={t('deleteProject')}
                        >
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            leaveProject(project.id);
                          }}
                          className="p-2 text-gray-400 hover:text-orange-600 hover:bg-white rounded-lg transition-colors"
                          title={t('leaveProject')}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Color Picker Dropdown */}
                {editingColor === project.id && (
                  <div className="px-4 pb-4 pt-2" style={{ backgroundColor: bgColor }}>
                    <div className="bg-white rounded-lg p-3 shadow-inner border border-gray-200">
                      <div className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">{t('chooseColor')}</div>
                      <div className="flex gap-2 flex-wrap">
                        {PROJECT_COLORS.map(color => (
                          <button
                            key={color.hex}
                            onClick={() => updateProjectColor(project.id, color.hex)}
                            className={`relative w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${
                              project.color === color.hex 
                                ? 'border-gray-900 scale-110 shadow-md' 
                                : 'border-gray-200 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color.hex }}
                            title={color.name}
                          >
                            {project.color === color.hex && (
                              <Check size={16} className="absolute inset-0 m-auto text-white drop-shadow" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Flowcharts in project */}
                {isExpanded && (
                  <div className="border-t" style={{ backgroundColor: bgColor }}>
                    {project.flowcharts.length === 0 && newFlowchartInProject !== project.id ? (
                      <div className="p-6 text-center">
                        <FileText size={32} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-400 text-sm font-medium">{filterMapType === 'mindmap' ? t('noMindmapsInProject') : t('noFlowchartsInProject')}</p>
                        <p className="text-gray-400 text-xs mt-1">{filterMapType === 'mindmap' ? t('clickPlusMindmap') : t('clickPlusFlowchart')}</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {project.flowcharts.map(flowchart => {
                          const fRoleBadge = getRoleBadge(flowchart.access_role);
                          const isActive = flowchart.id === currentFlowchartId;

                          return (
                            <div
                              key={flowchart.id}
                              className={`flex items-center gap-3 p-3 hover:bg-white hover:bg-opacity-60 transition-colors group ${
                                isActive ? 'bg-white bg-opacity-80 border-l-4 border-blue-500' : ''
                              }`}
                            >
                              <FileText size={16} className={`flex-shrink-0 ml-7 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                              <button
                                onClick={() => {
                                  onLoadFlowchart(flowchart as unknown as SavedFlowchart);
                                  onSetProject?.(project.id, project.name);
                                }}
                                className="flex-1 text-left text-sm hover:text-blue-600 transition-colors"
                              >
                                <div className={`font-medium ${isActive ? 'text-blue-600' : 'text-gray-800'}`}>
                                  {flowchart.title}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {new Date(flowchart.updated_at || flowchart.created_at).toLocaleString()}
                                </div>
                              </button>

                              <div className={`text-xs px-2 py-1 rounded-full font-medium ${fRoleBadge.color}`}>
                                {fRoleBadge.label}
                              </div>

                              {/* Move to another project or ungrouped */}
                              <select
                                onChange={(e) => {
                                  if (e.target.value !== '__current__') {
                                    moveFlowchart(flowchart.id, e.target.value || null);
                                  }
                                }}
                                className="text-xs px-2 py-1.5 border border-gray-300 rounded-lg hover:border-blue-400 focus:outline-none focus:border-blue-500 transition-colors opacity-0 group-hover:opacity-100 bg-white"
                                value="__current__"
                              >
                                <option value="__current__">{t('moveTo')}</option>
                                <option value="">{t('ungrouped')}</option>
                                {projects.filter(p => p.id !== project.id).map(p => (
                                  <option key={p.id} value={p.id}>📁 {p.name}</option>
                                ))}
                              </select>

                              {onRestoreVersion && (
                                <button
                                  onClick={() => openVersionHistory(flowchart)}
                                  className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                                  title={t('versionHistory')}
                                >
                                  <Clock size={16} />
                                </button>
                              )}
                              <button
                                onClick={() => duplicateFlowchart(flowchart)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                                title={t('duplicate')}
                              >
                                <Copy size={16} />
                              </button>
                              <button
                                onClick={() => flowchart.access_role === 'viewer' ? leaveFlowchart(flowchart.id) : handleDeleteFlowchart(flowchart.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                                title={flowchart.access_role === 'viewer' ? t('leaveFlowchart') : t('delete')}
                              >
                                <X size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Inline new flowchart input */}
                    {newFlowchartInProject === project.id && (
                      <div className="p-3 border-t border-gray-200 flex items-center gap-2">
                        <FileText size={16} className="text-blue-400 flex-shrink-0" />
                        <input
                          type="text"
                          value={newFlowchartTitle}
                          onChange={(e) => setNewFlowchartTitle(e.target.value)}
                          placeholder={filterMapType === 'mindmap' ? t('mindmapTitlePlaceholder') : t('flowchartTitlePlaceholder')}
                          className="flex-1 px-3 py-1.5 text-sm border-2 border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors bg-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') createFlowchartInProject(project.id);
                            if (e.key === 'Escape') { setNewFlowchartInProject(null); setNewFlowchartTitle(''); }
                          }}
                        />
                        <button
                          onClick={() => createFlowchartInProject(project.id)}
                          className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors font-medium"
                        >
                          {t('create')}
                        </button>
                        <button
                          onClick={() => { setNewFlowchartInProject(null); setNewFlowchartTitle(''); }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Ungrouped Flowcharts - Now collapsible like a project */}
          {ungroupedFlowcharts.length > 0 && (
            <div className="border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 transition-colors group">
                <button
                  onClick={() => toggleProject('ungrouped')}
                  className="text-gray-400 hover:text-gray-700 transition-colors"
                >
                  {expandedProjects.has('ungrouped') ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </button>
                <button
                  onClick={() => toggleProject('ungrouped')}
                  className="flex-1 text-left font-semibold flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  {expandedProjects.has('ungrouped') ? <FolderOpen size={18} /> : <Inbox size={18} />}
                  {t('ungrouped')}
                  <span className="text-xs text-gray-400 font-normal bg-white px-2 py-0.5 rounded-full">
                    {ungroupedFlowcharts.length}
                  </span>
                </button>
              </div>
              
              {expandedProjects.has('ungrouped') && (
                <div className="border-t bg-gray-50 divide-y divide-gray-200">
                  {ungroupedFlowcharts.map(flowchart => {
                    const fRoleBadge = getRoleBadge(flowchart.access_role);
                    const isActive = flowchart.id === currentFlowchartId;

                    return (
                      <div
                        key={flowchart.id}
                        className={`flex items-center gap-3 p-3 hover:bg-white transition-colors group ${
                          isActive ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                      >
                        <FileText size={16} className={`flex-shrink-0 ml-7 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                        <button
                          onClick={() => {
                            onLoadFlowchart(flowchart as unknown as SavedFlowchart);
                            onSetProject?.(null, null);
                          }}
                          className="flex-1 text-left text-sm hover:text-blue-600 transition-colors"
                        >
                          <div className={`font-medium ${isActive ? 'text-blue-600' : 'text-gray-800'}`}>
                            {flowchart.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(flowchart.updated_at || flowchart.created_at).toLocaleString()}
                          </div>
                        </button>

                        <div className={`text-xs px-2 py-1 rounded-full font-medium ${fRoleBadge.color}`}>
                          {fRoleBadge.label}
                        </div>

                        {/* Move to project dropdown */}
                        {projects.length > 0 && (
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                moveFlowchart(flowchart.id, e.target.value);
                              }
                            }}
                            className="text-xs px-2 py-1.5 border border-gray-300 rounded-lg hover:border-blue-400 focus:outline-none focus:border-blue-500 transition-colors opacity-0 group-hover:opacity-100 bg-white"
                            defaultValue=""
                          >
                            <option value="">{t('moveTo')}</option>
                            {projects.map(p => (
                              <option key={p.id} value={p.id}>📁 {p.name}</option>
                            ))}
                          </select>
                        )}

                        {onRestoreVersion && (
                          <button
                            onClick={() => openVersionHistory(flowchart)}
                            className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                            title="Versiegeschiedenis"
                          >
                            <Clock size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => duplicateFlowchart(flowchart)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                          title={t('duplicate')}
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          onClick={() => flowchart.access_role === 'viewer' ? leaveFlowchart(flowchart.id) : handleDeleteFlowchart(flowchart.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                          title={flowchart.access_role === 'viewer' ? t('leaveFlowchart') : t('delete')}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Trash */}
          {trashedFlowcharts.length > 0 && (
            <div className="border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow">
              <div
                className="flex items-center gap-3 p-4 bg-red-50 hover:bg-red-100 transition-colors cursor-pointer"
                onClick={() => setShowTrash(!showTrash)}
              >
                <Trash2 size={18} className="text-red-400" />
                <span className="flex-1 font-semibold text-red-700">{t('trash')}</span>
                <span className="text-xs text-red-400 bg-white px-2 py-0.5 rounded-full">{trashedFlowcharts.length}</span>
                {showTrash ? <ChevronDown size={16} className="text-red-400" /> : <ChevronRight size={16} className="text-red-400" />}
              </div>
              {showTrash && (
                <div className="border-t bg-red-50 divide-y divide-red-100">
                  {trashedFlowcharts.map((f) => {
                    const daysLeft = Math.ceil((new Date(f.deleted_at).getTime() + 30 * 86400000 - Date.now()) / 86400000);
                    return (
                    <div key={f.id} className="flex items-center gap-3 p-3 hover:bg-red-100 transition-colors">
                      <FileText size={16} className="text-red-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-700 truncate">{f.title}</div>
                        <div className="text-xs text-red-400">
                          {t('deletedAgo', { date: new Date(f.deleted_at).toLocaleDateString() })} · {daysLeft > 0 ? t('daysLeft', { days: daysLeft }) : t('deletingSoon')}
                        </div>
                      </div>
                      <button
                        onClick={() => restoreFlowchart(f.id)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors"
                        title={t('restore')}
                      >
                        <RotateCcw size={12} />
                        {t('restore')}
                      </button>
                      <button
                        onClick={() => permanentDeleteFlowchart(f.id)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-200 rounded transition-colors"
                        title={t('deletePermanently')}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );})}
                  <div className="p-3 text-xs text-red-400 text-center">
                    {t('autoDeleteInfo')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {projects.length === 0 && ungroupedFlowcharts.length === 0 && (
            <div className="text-center py-16 px-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12 border-2 border-dashed border-blue-200">
                <Folder size={64} className="mx-auto mb-4 text-blue-300" />
                {filterMapType === 'mindmap' ? (
                  <>
                    <h3 className="text-xl font-bold text-gray-700 mb-2">{t('noMindmapsYet')}</h3>
                    <p className="text-gray-500">{t('createFirstMindmapDesc')}</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-gray-700 mb-2">{t('noProjectsYet')}</h3>
                    <p className="text-gray-500 mb-6">{t('createFirstProjectDesc')}</p>
                    <button
                      onClick={() => setShowNewProject(true)}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium shadow-sm"
                    >
                      <Plus size={20} />
                      {t('createFirstProject')}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && <ConfirmDialog {...confirmDialog} />}

      {/* Version History sub-modal */}
      {versionHistoryFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setVersionHistoryFor(null)}>
          <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', width: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff7ed' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Clock size={16} color="#f97316" />
                <span style={{ fontWeight: 700, fontSize: 14 }}>{t('versionHistory')}</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>— {versionHistoryFor.title}</span>
              </div>
              <button onClick={() => setVersionHistoryFor(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={16} /></button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              {loadingVersions ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>{t('loading')}</div>
              ) : versionItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>
                  <Clock size={36} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.3 }} />
                  <p style={{ fontSize: 13 }}>{t('noSavedVersions')}</p>
                </div>
              ) : versionItems.map((v, i) => (
                <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{v.title || t('untitled')}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {new Date(v.saved_at).toLocaleString()}
                      {i === 0 && <span style={{ marginLeft: 8, color: '#10b981', fontWeight: 600 }}>{t('latest')}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => restoreVersion(v.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: 'linear-gradient(to bottom,#fb923c,#f97316)', border: 'none', borderRadius: 6, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    <RotateCcw size={12} /> {t('restore')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Share Project Dialog */}
      {sharingProject && (
        <ShareProjectDialog
          projectId={sharingProject.id}
          projectName={sharingProject.name}
          projectColor={sharingProject.color}
          user={user}
          onClose={() => setSharingProject(null)}
        />
      )}
    </div>
  );
}
